import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import SimulatorGate from "@/components/modules/SimulatorGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import FounderDashboard from "@/components/entrepreneurship/FounderDashboard";
import EntKeyTermsButton from "@/components/entrepreneurship/EntKeyTermsButton";
import { ARCHETYPE_IMAGES } from "@/components/entrepreneurship/archetypeImages";
import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ArrowRight, Briefcase, Award, AlertTriangle, Sparkles, Building2, Rocket,
  TrendingUp, FileText,
} from "lucide-react";
import {
  BUSINESS_TYPES, BusinessTypeKey, AUDIENCE_INFO, PRICING_INFO, STRATEGY_INFO,
  DECISION_PANELS, DecisionPanelKey, DecisionOption, CRUNCH_OPTIONS,
  initialState, configureBusiness, applyBudget, applyEffects, runMonth,
  pickEvent, derived, founderRating,
  type SimState, type Audience, type Pricing, type Strategy, type SimEvent,
} from "@/data/entrepreneurshipSim";
import {
  newAttemptId, startModuleAttempt, commitModuleAttempt, trackModule, trackAccountEffect,
} from "@/lib/learning/telemetry";
import { ENTREPRENEURSHIP_EVENTS as EV, MODULE_CONCEPT_TAGS } from "@/lib/learning/moduleEvents";

/* ─── Step keys for ModuleShell progress bar ─── */

type Step =
  | "brief" | "type" | "create" | "budget"
  | "operate" | "crunch" | "report" | "quiz" | "summary";
const STEP_ORDER: Step[] = ["brief","type","create","budget","operate","crunch","report","quiz","summary"];
const PHASE_LABEL: Record<Step, string> = {
  brief: "Brief", type: "Business Type", create: "Create",
  budget: "Allocate Capital", operate: "Operate", crunch: "Cash Crunch",
  report: "Founder Report", quiz: "Knowledge Check", summary: "Complete",
};

const money = (n: number) =>
  (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString();

/* ─── Reusable choice list (matches CreditManagement style) ─── */

function ChoiceList({
  options, value, onChange,
}: { options: DecisionOption[]; value: string | null; onChange: (k: string) => void }) {
  return (
    <div className="grid gap-3">
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`rounded-2xl border p-4 text-left transition-all ${
              active ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"
            }`}
          >
            <p className="font-heading text-sm font-bold text-foreground">{o.label}</p>
            <ul className="mt-2 space-y-1">
              {o.bullets.map((b, i) => (
                <li key={i} className="text-[11px] text-foreground/75">• {b}</li>
              ))}
            </ul>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Main content ─── */

const EntrepreneurshipContent = () => {
  const navigate = useNavigate();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();

  const [sim, setSim] = useState<SimState>(() => initialState());
  const [step, setStep] = useState<Step>("brief");
  const [completed, setCompleted] = useState(false);

  // Creation form
  const [pickedType, setPickedType] = useState<BusinessTypeKey | null>(null);
  const [name, setName] = useState("");
  const [audience, setAudience] = useState<Audience>("general");
  const [pricing, setPricing] = useState<Pricing>("balanced");
  const [strategy, setStrategy] = useState<Strategy>("balanced");

  // Decision/event scratch state
  const [activePanel, setActivePanel] = useState<DecisionPanelKey | null>(null);
  const [decisionChoice, setDecisionChoice] = useState<string | null>(null);
  const [eventChoice, setEventChoice] = useState<string | null>(null);
  const attemptRef = useRef(newAttemptId("entrepreneurship"));
  const startedRef = useRef(false);
  const startTelemetry = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    void startModuleAttempt({
      moduleSlug: "entrepreneurship",
      attemptId: attemptRef.current,
      conceptTags: MODULE_CONCEPT_TAGS.entrepreneurship,
    });
  };

  const gate = useMemo(
    () => moduleGates.find((g) => g.moduleKey === "entrepreneurship"),
    [moduleGates],
  );
  const isLocked = !gate?.isUnlocked;
  const lockMsg = gate?.missingPrereqs?.length
    ? `Complete: ${gate.missingPrereqs.join(", ")}`
    : "Complete the prerequisite lessons to unlock.";

  if (loading) return null;

  /* ── Phase transitions ── */

  const onPickType = (key: BusinessTypeKey) => {
    startTelemetry();
    void trackModule({
      eventType: EV.TYPE_SELECTED,
      moduleSlug: "entrepreneurship",
      attemptId: attemptRef.current,
      metadata: { business_type: key },
    });
    setPickedType(key);
    setStep("create");
  };

  const onCreate = () => {
    if (!pickedType || !name.trim()) return;
    const next = configureBusiness(initialState(), pickedType, name.trim(), audience, pricing, strategy);
    setSim(next);
    void trackModule({
      eventType: EV.CREATED,
      moduleSlug: "entrepreneurship",
      attemptId: attemptRef.current,
      metadata: { business_name: name.trim(), business_type: pickedType, cash: next.cash },
    });
    void trackModule({
      eventType: EV.AUDIENCE, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      metadata: { decision_choice: audience },
    });
    void trackModule({
      eventType: EV.PRICING, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      metadata: { decision_choice: pricing },
    });
    void trackModule({
      eventType: EV.STRATEGY, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      metadata: { decision_choice: strategy },
    });
    setStep("budget");
  };

  const onConfirmBudget = () => {
    const next = applyBudget(sim);
    setSim(next);
    void trackModule({
      eventType: EV.BUDGET, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      metadata: { allocations: next.allocations, cash: next.cash },
    });
    setStep("operate");
  };

  // Operating sub-phase
  const onOpenPanel = (k: DecisionPanelKey) => {
    setActivePanel(k);
    setDecisionChoice(null);
  };

  const onSubmitDecision = () => {
    if (!activePanel || !decisionChoice) return;
    const opt = DECISION_PANELS[activePanel].options.find((o) => o.key === decisionChoice);
    if (!opt) return;
    const next = applyEffects(sim, opt.effects);
    next.decisionThisMonth = { panel: activePanel, optionKey: opt.key, note: opt.effects.note };
    next.pendingEvent = pickEvent(next);
    next.phase = "event";
    setSim(next);
    void trackModule({
      eventType: EV.DECISION, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      sourceId: `ent:${attemptRef.current}:m${sim.month}:${activePanel}`,
      metadata: { current_month: sim.month, decision_choice: opt.key, panel: activePanel },
    });
    if (next.pendingEvent) {
      void trackModule({
        eventType: EV.EVENT_TRIGGERED, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
        sourceId: `ent:${attemptRef.current}:m${sim.month}:${next.pendingEvent.id}`,
        metadata: { current_month: sim.month, event_type: next.pendingEvent.id },
      });
    }
    setActivePanel(null);
    setDecisionChoice(null);
    setEventChoice(null);
  };

  const onResolveEvent = () => {
    if (!sim.pendingEvent || !eventChoice) return;
    const opt = sim.pendingEvent.options.find((o) => o.key === eventChoice);
    if (!opt) return;
    let next = applyEffects(sim, opt.effects);
    next.eventThisMonth = { eventId: sim.pendingEvent.id, optionKey: opt.key, note: opt.effects.note };
    next.pendingEvent = undefined;
    next = runMonth(next);
    setSim(next);
    void trackModule({
      eventType: EV.EVENT_RESOLVED, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      sourceId: `ent:${attemptRef.current}:m${sim.month}:resolve`,
      metadata: { current_month: sim.month, decision_choice: opt.key },
    });
    void trackAccountEffect({
      moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      eventType: "business_cash_changed", status: "draft",
      amount: next.cash - sim.cash, account: "business_cash",
      metadata: { current_month: next.month, revenue: next.totalRevenue, expenses: next.totalExpenses },
    });
    setEventChoice(null);
  };

  const onAdvanceMonth = () => {
    // Check cash crunch
    if ((sim.cash < 0 || derived(sim).runway < 1) && !sim.crunchUsed && sim.status === "active") {
      setSim({ ...sim, phase: "crunch" });
      void trackModule({
        eventType: EV.CRUNCH_TRIGGERED, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
        metadata: { current_month: sim.month, cash: sim.cash, runway_months: derived(sim).runway },
      });
      setStep("crunch");
      return;
    }
    if (sim.month >= sim.maxMonths || sim.status !== "active") {
      setStep("report");
      return;
    }
    setSim({
      ...sim,
      month: sim.month + 1,
      decisionThisMonth: undefined,
      eventThisMonth: undefined,
      monthResult: undefined,
      phase: "monthStart",
    });
    void trackModule({
      eventType: EV.MONTH_STARTED, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      sourceId: `ent:${attemptRef.current}:m${sim.month + 1}`,
      metadata: { current_month: sim.month + 1 },
    });
  };

  const onCrunchChoose = (key: string) => {
    const opt = CRUNCH_OPTIONS.find((o) => o.key === key);
    if (!opt) return;
    const next = applyEffects(sim, opt.effects);
    next.crunchUsed = true;
    void trackModule({
      eventType: EV.CRUNCH_RESOLVED, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      metadata: { decision_choice: key },
    });
    if (key === "shutdown") {
      next.status = "shutdown";
      setSim(next);
      setStep("report");
      return;
    }
    next.phase = "monthStart";
    setSim(next);
    setStep("operate");
  };

  const onQuizPass = () => {
    setCompleted(true);
    completeModule("entrepreneurship");
    const profit = sim.totalRevenue - sim.totalExpenses;
    void trackModule({
      eventType: EV.FINAL_REPORT, moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      metadata: {
        business_name: sim.name, business_type: sim.businessType, current_month: sim.month,
        cash: sim.cash, revenue: sim.totalRevenue, expenses: sim.totalExpenses, profit,
        customers: sim.customers, satisfaction: sim.satisfaction, brand: sim.brandTrust,
        burnout: sim.burnout, runway_months: derived(sim).runway,
        founder_rating: founderRating(sim), business_status: sim.status,
      },
    });
    void commitModuleAttempt({
      moduleSlug: "entrepreneurship", attemptId: attemptRef.current,
      metadata: { business_type: sim.businessType, profit, founder_rating: founderRating(sim) },
    });
    setStep("summary");
  };

  /* ── Render helpers ── */

  const HUD = (
    <div className="space-y-3 mb-4">
      <FounderDashboard s={sim} phase={PHASE_LABEL[step]} />
      <div className="flex justify-end">
        <EntKeyTermsButton />
      </div>
    </div>
  );

  const totalAlloc = sim.allocations
    ? Object.values(sim.allocations).reduce((a, b) => a + b, 0)
    : 0;

  /* ── Render ── */

  return (
    <ModuleShell
      title="Entrepreneurship"
      stepIndex={STEP_ORDER.indexOf(step)}
      totalSteps={STEP_ORDER.length}
      isLocked={isLocked}
      lockMessage={lockMsg}
      isCompleted={completed}
      completedMessage="You operated a simulated business end-to-end and passed the knowledge check."
      stepKey={`${step}:${sim.month}:${sim.phase}`}
    >
      {/* BRIEF */}
      {step === "brief" && (
        <ModuleStepCard
          icon={<Rocket className="w-8 h-8 text-primary" />}
          title="You're the Founder"
          body="You have limited startup capital. Your job is to turn cash into customers without running out of money. Every decision moves cash, customers, satisfaction, brand, burnout, and runway."
          highlights={[
            "Each month: make a decision → handle an event → see results",
            "Cash runway, not revenue, decides survival",
            "Pass the 5-question check (4/5) to commit your outcome",
          ]}
          action={
            <Button onClick={() => setStep("type")} className="w-full gap-2">
              Choose a Business Type <ArrowRight className="w-4 h-4" />
            </Button>
          }
        />
      )}

      {/* BUSINESS TYPE */}
      {step === "type" && (
        <BusinessTypeCarousel
          pickedType={pickedType}
          onSelect={onPickType}
        />
      )}

      {/* CREATE */}
      {step === "create" && pickedType && (
        <div className="space-y-5">
          <ModuleStepCard
            icon={<Briefcase className="w-8 h-8 text-primary" />}
            title="Create Your Business"
            body={`You're launching a ${BUSINESS_TYPES[pickedType].name}. Each choice changes your starting metrics and economics.`}
            action={<div />}
          />
          <div className="space-y-3">
            <label className="text-xs text-muted-foreground">Business name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sage Lawn Co." />
          </div>
          <Section title="Target Audience">
            {(Object.keys(AUDIENCE_INFO) as Audience[]).map((k) => (
              <RadioCard key={k} active={audience === k} onClick={() => setAudience(k)}
                title={AUDIENCE_INFO[k].label} subtitle={AUDIENCE_INFO[k].summary} />
            ))}
          </Section>
          <Section title="Pricing Style">
            {(Object.keys(PRICING_INFO) as Pricing[]).map((k) => (
              <RadioCard key={k} active={pricing === k} onClick={() => setPricing(k)}
                title={PRICING_INFO[k].label} subtitle={PRICING_INFO[k].summary} />
            ))}
          </Section>
          <Section title="Startup Strategy">
            {(Object.keys(STRATEGY_INFO) as Strategy[]).map((k) => (
              <RadioCard key={k} active={strategy === k} onClick={() => setStrategy(k)}
                title={STRATEGY_INFO[k].label} subtitle={STRATEGY_INFO[k].summary} />
            ))}
          </Section>
          <Button onClick={onCreate} disabled={!name.trim()} className="w-full gap-2">
            Continue to Capital Allocation <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* BUDGET */}
      {step === "budget" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<TrendingUp className="w-8 h-8 text-accent" />}
            title="Allocate Your Startup Capital"
            body={`You have ${money(sim.startingCash)} to launch. Underfunding any bucket creates real risk later.`}
            action={<div />}
          />
          <div className="space-y-3">
            {(Object.keys(sim.allocations) as Array<keyof SimState["allocations"]>).map((k) => {
              const v = sim.allocations[k];
              return (
                <div key={k} className="rounded-xl border border-border bg-card/60 p-3">
                  <div className="flex justify-between text-xs">
                    <span className="font-heading font-bold text-foreground capitalize">{k}</span>
                    <span className="font-mono text-muted-foreground">
                      {v}% · {money(sim.startingCash * v / 100)}
                    </span>
                  </div>
                  <Slider
                    value={[v]} min={0} max={60} step={5}
                    onValueChange={([n]) => setSim({ ...sim, allocations: { ...sim.allocations, [k]: n } })}
                    className="mt-2"
                  />
                </div>
              );
            })}
            <p className={`text-xs text-center font-mono ${
              totalAlloc === 100 ? "text-emerald-400" : "text-amber-400"
            }`}>
              Total: {totalAlloc}% {totalAlloc === 100 ? "✓" : "(must equal 100%)"}
            </p>
          </div>
          <Button onClick={onConfirmBudget} disabled={totalAlloc !== 100} className="w-full gap-2">
            Launch the Business <Rocket className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* OPERATE */}
      {step === "operate" && (
        <div className="space-y-5">
          {HUD}

          {/* monthStart / decision picker */}
          {(sim.phase === "monthStart" || sim.phase === "decision") && !activePanel && (
            <>
              <ModuleStepCard
                icon={<Sparkles className="w-8 h-8 text-primary" />}
                title={`Month ${sim.month} — Make Your Move`}
                body="Pick one decision panel. Each option pushes the business in a different direction."
                action={<div />}
              />
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(DECISION_PANELS) as DecisionPanelKey[]).map((k) => {
                  // Gate hiring/reinvest/expand by phase
                  const restricted =
                    (k === "reinvest" && sim.month < 4) ||
                    (k === "expand"   && sim.month < 8);
                  return (
                    <button
                      key={k}
                      disabled={restricted}
                      onClick={() => onOpenPanel(k)}
                      className={`rounded-2xl border p-3 text-left transition-all ${
                        restricted ? "opacity-40 cursor-not-allowed border-border/40 bg-card/30"
                        : "border-border bg-card/60 hover:border-primary/40"
                      }`}
                    >
                      <p className="font-heading text-xs font-bold text-foreground capitalize">{DECISION_PANELS[k].title}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {restricted ? "Unlocks later" : DECISION_PANELS[k].prompt}
                      </p>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* decision panel open */}
          {activePanel && (
            <div className="space-y-4">
              <ModuleStepCard
                icon={<Briefcase className="w-8 h-8 text-primary" />}
                title={DECISION_PANELS[activePanel].title}
                body={DECISION_PANELS[activePanel].prompt}
                action={<div />}
              />
              <ChoiceList
                options={DECISION_PANELS[activePanel].options}
                value={decisionChoice}
                onChange={setDecisionChoice}
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setActivePanel(null)} className="flex-1">Back</Button>
                <Button onClick={onSubmitDecision} disabled={!decisionChoice} className="flex-1 gap-2">
                  Commit Decision <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* event */}
          {sim.phase === "event" && sim.pendingEvent && (
            <div className="space-y-4">
              <ModuleStepCard
                icon={<AlertTriangle className="w-8 h-8 text-amber-400" />}
                title={sim.pendingEvent.title}
                body={sim.pendingEvent.body}
                action={<div />}
              />
              <ChoiceList
                options={sim.pendingEvent.options}
                value={eventChoice}
                onChange={setEventChoice}
              />
              <Button onClick={onResolveEvent} disabled={!eventChoice} className="w-full gap-2">
                Resolve Event <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* debrief */}
          {sim.phase === "debrief" && sim.monthResult && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-primary/30 bg-card/70 p-4 space-y-1.5 text-xs">
                <p className="font-heading text-sm font-bold text-foreground mb-2">Month {sim.month} Results</p>
                <Row label="Leads"          value={`${sim.monthResult.leads}`} />
                <Row label="New customers"  value={`${sim.monthResult.newCustomers}`} />
                <Row label="Revenue"        value={money(sim.monthResult.revenue)} />
                <Row label="Variable cost"  value={money(sim.monthResult.variable)} />
                <Row label="Fixed cost"     value={money(sim.monthResult.fixed)} />
                <Row label="Profit"         value={money(sim.monthResult.profit)} bold
                     valueClass={sim.monthResult.profit >= 0 ? "text-emerald-400" : "text-destructive"} />
              </div>
              {(sim.decisionThisMonth?.note || sim.eventThisMonth?.note) && (
                <div className="rounded-2xl border border-border bg-card/60 p-3 text-[11px] text-foreground/80 space-y-1">
                  {sim.decisionThisMonth?.note && <p>· {sim.decisionThisMonth.note}</p>}
                  {sim.eventThisMonth?.note && <p>· {sim.eventThisMonth.note}</p>}
                </div>
              )}
              <Button onClick={onAdvanceMonth} className="w-full gap-2">
                {sim.month >= sim.maxMonths ? "See Founder Report" : "Continue to Next Month"} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* CRUNCH */}
      {step === "crunch" && (
        <div className="space-y-5">
          {HUD}
          <ModuleStepCard
            icon={<AlertTriangle className="w-8 h-8 text-destructive" />}
            title="Cash Crunch"
            body="You're out of runway. Real founders face this. Pick a survival move."
            action={<div />}
          />
          <ChoiceList options={CRUNCH_OPTIONS} value={null} onChange={onCrunchChoose} />
        </div>
      )}

      {/* REPORT */}
      {step === "report" && (
        <FounderReport sim={sim} onContinue={() => setStep("quiz")} />
      )}

      {/* QUIZ */}
      {step === "quiz" && (
        <ModuleQuiz moduleKey="entrepreneurship" sampleSize={5} shuffleOptions onPass={onQuizPass} />
      )}

      {/* SUMMARY */}
      {step === "summary" && (
        <ModuleStepCard
          icon={<Award className="w-8 h-8 text-primary" />}
          title="Module Complete"
          body={`${sim.name} closed at ${money(sim.cash)} cash, ${sim.customers} customers, and a ${Math.round(derived(sim).health)}/100 business health score.`}
          highlights={[
            "Outcome saved to your profile",
            "XP and rewards awarded",
            "Visit Financial Command Center for the rundown",
          ]}
          action={
            <Button onClick={() => navigate("/finances")} className="w-full gap-2">
              Open Financial Command Center <ArrowRight className="w-4 h-4" />
            </Button>
          }
        />
      )}
    </ModuleShell>
  );
};

/* ─── Small helpers ─── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-heading font-bold text-muted-foreground uppercase tracking-wide">{title}</p>
      <div className="grid gap-2">{children}</div>
    </div>
  );
}

function RadioCard({ active, onClick, title, subtitle }:
  { active: boolean; onClick: () => void; title: string; subtitle: string }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl border p-3 text-left transition-all ${
        active ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"
      }`}
    >
      <p className="font-heading text-sm font-bold text-foreground">{title}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</p>
    </button>
  );
}

function Row({ label, value, bold, valueClass }:
  { label: string; value: string; bold?: boolean; valueClass?: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className={`text-muted-foreground ${bold ? "font-heading font-bold text-foreground" : ""}`}>{label}</span>
      <span className={`font-mono ${bold ? "font-heading font-bold text-foreground" : "text-foreground"} ${valueClass ?? ""}`}>{value}</span>
    </div>
  );
}

/* ─── Founder Report ─── */

function FounderReport({ sim, onContinue }: { sim: SimState; onContinue: () => void }) {
  const rating = founderRating(sim);
  const d = derived(sim);
  return (
    <div className="space-y-4">
      <ModuleStepCard
        icon={<FileText className="w-8 h-8 text-primary" />}
        title="Founder Report"
        body={`${sim.name} (${sim.businessType}) — ${sim.month} months operated.`}
        action={<div />}
      />
      <div className={`rounded-2xl border p-4 ${
        rating.tone === "good" ? "border-emerald-500/40 bg-emerald-500/10"
        : rating.tone === "warn" ? "border-amber-500/40 bg-amber-500/10"
        : "border-destructive/40 bg-destructive/10"
      }`}>
        <p className="font-heading text-base font-bold text-foreground">{rating.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{rating.body}</p>
      </div>
      <div className="rounded-2xl border border-border bg-card/60 p-4 text-xs space-y-1.5">
        <Row label="Final cash"          value={money(sim.cash)} />
        <Row label="Total revenue"       value={money(sim.totalRevenue)} />
        <Row label="Total expenses"      value={money(sim.totalExpenses)} />
        <Row label="Net profit/loss"     value={money(sim.totalRevenue - sim.totalExpenses)} bold
             valueClass={sim.totalRevenue - sim.totalExpenses >= 0 ? "text-emerald-400" : "text-destructive"} />
        <Row label="Customers"           value={`${sim.customers}`} />
        <Row label="Satisfaction"        value={`${Math.round(sim.satisfaction)}/100`} />
        <Row label="Brand trust"         value={`${Math.round(sim.brandTrust)}/100`} />
        <Row label="Burnout"             value={`${Math.round(sim.burnout)}/100`} />
        <Row label="Runway left"         value={`${d.runway.toFixed(1)} mo`} />
        <Row label="Business health"     value={`${Math.round(d.health)}/100`} />
        <Row label="Status"              value={sim.status} />
      </div>
      {(sim.bestDecision || sim.worstDecision) && (
        <div className="rounded-2xl border border-border bg-card/60 p-4 text-xs space-y-1">
          {sim.bestDecision && <p>· <span className="text-emerald-400">Best month decision:</span> {sim.bestDecision.label} ({money(sim.bestDecision.profitImpact)})</p>}
          {sim.worstDecision && <p>· <span className="text-destructive">Worst month decision:</span> {sim.worstDecision.label} ({money(sim.worstDecision.profitImpact)})</p>}
        </div>
      )}
      <Button onClick={onContinue} className="w-full gap-2">
        Take the Knowledge Check <ArrowRight className="w-4 h-4" />
      </Button>
      <p className="text-[10px] text-muted-foreground/70 italic text-center">
        Pass 4/5 to commit this outcome. Fail and the module effects are voided.
      </p>
    </div>
  );
}

/* ─── Export wrapper ─── */

const EntrepreneurshipSimulator = () => (
  <SimulatorGate moduleKey="entrepreneurship">
    <EntrepreneurshipContent />
  </SimulatorGate>
);

export default EntrepreneurshipSimulator;

/* ─── Business Type Horizontal Rail ─── */

function BusinessTypeCarousel({
  pickedType,
  onSelect,
}: {
  pickedType: BusinessTypeKey | null;
  onSelect: (k: BusinessTypeKey) => void;
}) {
  const types = Object.values(BUSINESS_TYPES);
  const [selected, setSelected] = useState<BusinessTypeKey | null>(pickedType);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ active: boolean; startX: number; startScroll: number; moved: number }>({
    active: false, startX: 0, startScroll: 0, moved: 0,
  });

  useEffect(() => { setSelected(pickedType); }, [pickedType]);

  const scrollBy = (dx: number) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Only hijack mouse/pen drags. Let native touch scrolling handle fingers
    // so taps reliably register as clicks on the inner card buttons.
    if (e.pointerType === "touch") return;
    dragRef.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: 0 };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el || !dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    dragRef.current.moved = Math.abs(dx);
    if (dragRef.current.moved > 6) {
      try { el.setPointerCapture(e.pointerId); } catch { /* non-critical: failure is safe to ignore here */ }
    }
    el.scrollLeft = dragRef.current.startScroll - dx;
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    dragRef.current.active = false;
    try { el.releasePointerCapture(e.pointerId); } catch { /* non-critical: failure is safe to ignore here */ }
    // Reset shortly so the next click isn't suppressed by a stale `moved` value.
    setTimeout(() => { dragRef.current.moved = 0; }, 0);
  };

  const activeBT = selected ? BUSINESS_TYPES[selected] : null;

  return (
    <div className="space-y-5">
      <ModuleStepCard
        icon={<Building2 className="w-8 h-8 text-primary" />}
        title="Pick Your Business Model"
        body="Each model has different economics, risks, and metrics. Scroll through and tap a card to select it."
        action={<div />}
      />

      <div className="relative left-1/2 -translate-x-1/2 w-screen max-w-[100vw]">
        {/* edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent z-10" />

        {/* arrow controls (desktop) */}
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollBy(-320)}
          className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full border border-border bg-card/80 backdrop-blur hover:border-primary/60"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollBy(320)}
          className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full border border-border bg-card/80 backdrop-blur hover:border-primary/60"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        <div
          ref={scrollerRef}
          className="flex items-stretch gap-4 overflow-x-auto overscroll-x-contain touch-pan-x px-6 sm:px-12 py-2 snap-x snap-mandatory cursor-grab active:cursor-grabbing select-none [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {types.map((bt) => {
            const isActive = selected === bt.key;
            return (
              <button
                key={bt.key}
                onClick={() => {
                  if (dragRef.current.moved > 6) return;
                  setSelected(bt.key);
                }}
                onDragStart={(e) => e.preventDefault()}
                className={`group relative shrink-0 w-[280px] sm:w-[300px] lg:w-[320px] snap-start rounded-2xl border overflow-hidden text-left transition-all duration-300 ${
                  isActive
                    ? "border-primary bg-card shadow-[0_0_30px_-8px_hsl(var(--primary)/0.6)] ring-2 ring-primary/50"
                    : "border-border/60 bg-card/60 hover:border-primary/40 hover:bg-card/80"
                }`}
              >
                {ARCHETYPE_IMAGES[bt.key] && (
                  <div className="aspect-[16/10] w-full overflow-hidden bg-muted/40">
                    <img
                      src={ARCHETYPE_IMAGES[bt.key]}
                      alt={`${bt.name} business illustration`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-4 space-y-2.5">
                  <p className="font-heading text-base font-bold text-foreground">{bt.name}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{bt.examples}</p>
                  <div className="flex flex-wrap gap-1 text-[10px] font-mono">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">Diff {bt.difficulty}</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">Risk {bt.riskLabel}</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">Growth {bt.growthLabel}</span>
                  </div>
                  <div className="pt-1 border-t border-border/40 space-y-1 text-[11px] text-foreground/80">
                    <p>Startup <span className="font-mono text-foreground">{money(bt.startupCost)}</span></p>
                    <p className="text-amber-400/90 line-clamp-2">Tradeoff: {bt.hiddenTradeoff}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Button
        onClick={() => selected && onSelect(selected)}
        disabled={!selected}
        className="w-full gap-2"
        size="lg"
      >
        Continue with {activeBT?.name || "Selection"}
        <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
}
